import { useState, useRef, useEffect, useCallback, memo, useMemo } from "react";
import PropTypes from "prop-types";
import { X, Volume2, VolumeX, Pause, Play } from "lucide-react";
import recipeData from "../../assets/recipes";
import storyData from "../../assets/storyData.json";
import "./storybuilder.css";

const STORAGE_KEY = "storyViewerSeenIds";
const KNOWN_STORY_IDS_KEY = "storyViewerKnownStoryIds";
const PROGRESS_UPDATE_INTERVAL = 100;
const VIDEO_PRELOAD_STRATEGY = "auto"; // "none", "metadata", "auto"
const KEYBOARD_THROTTLE_MS = 200;

const getPersistedSeenIds = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    console.warn("Failed to load seen IDs from localStorage:", error);
    return [];
  }
};

const setPersistedSeenIds = (ids) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch (error) {
    console.warn("Failed to save seen IDs to localStorage:", error);
  }
};

const getPersistedKnownStoryIds = () => {
  try {
    const raw = window.localStorage.getItem(KNOWN_STORY_IDS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn("Failed to load known story IDs from localStorage:", error);
    return null;
  }
};

const setPersistedKnownStoryIds = (ids) => {
  try {
    window.localStorage.setItem(KNOWN_STORY_IDS_KEY, JSON.stringify(ids));
  } catch (error) {
    console.warn("Failed to save known story IDs to localStorage:", error);
  }
};

const loadPeopleState = () => {
  const seenIds = new Set(getPersistedSeenIds());
  const validPeople = validateStoryData();

  const allStoryIds = validPeople.flatMap((person) =>
    person.stories.map((s) => s.id),
  );

  let knownIds = getPersistedKnownStoryIds();
  if (knownIds === null) {

    knownIds = allStoryIds;
    setPersistedKnownStoryIds(knownIds);
  }
  const knownIdSet = new Set(knownIds);

  return validPeople.map((person) => ({
    ...person,
    seen: seenIds.has(person.id),
    isNew: person.stories.some((s) => !knownIdSet.has(s.id)),
  }));
};

const findRecipeByKey = (key) => {
  if (!key) return null;

  const cacheKey = String(key).trim().toLowerCase();
  const recipes = recipeData?.recipes || [];
  if (!Array.isArray(recipes) || recipes.length === 0) {
    return null;
  }

  const exact = recipes.find((recipe) => {
    const values = [recipe.title, recipe.name, recipe.recipe_name].filter(
      Boolean,
    );
    return values.some(
      (value) => String(value).trim().toLowerCase() === cacheKey,
    );
  });

  if (exact) return exact;

  return (
    recipes.find((recipe) => {
      const values = [recipe.title, recipe.name, recipe.recipe_name].filter(
        Boolean,
      );
      return values.some((value) =>
        String(value).trim().toLowerCase().includes(cacheKey),
      );
    }) || null
  );
};

const validateStoryData = () => {
  if (!storyData?.people || !Array.isArray(storyData.people)) {
    console.warn("Invalid story data structure");
    return [];
  }
  return storyData.people.filter((person) => {
    const valid = person?.id && person?.name && Array.isArray(person?.stories);
    if (!valid) {
      console.warn("Skipping invalid person entry:", person);
    }
    return valid;
  });
};

const prefetchedVideos = new Set();
const prefetchVideo = (src) => {
  if (!src || prefetchedVideos.has(src)) return;
  prefetchedVideos.add(src);
  const video = document.createElement("video");
  video.src = src;
  video.preload = "auto";
  video.muted = true;
  video.style.display = "none";
  document.body.appendChild(video);
  setTimeout(() => video.remove(), 15000);
};

const StoryRing = memo(({ person, onOpen }) => {
  const handleClick = useCallback(() => {
    if (person && onOpen) {
      onOpen(person);
    }
  }, [person, onOpen]);

  const handleImageError = useCallback(
    (e) => {
      console.warn(`Failed to load avatar for ${person.name}`);
      e.target.style.display = "none";
    },
    [person.name],
  );

  const handleWarmup = useCallback(() => {
    const firstVideo = person?.stories?.[0]?.video;
    if (firstVideo) prefetchVideo(firstVideo);
  }, [person]);

  if (!person) return null;

  return (
    <button
      className="story-ring"
      onClick={handleClick}
      onPointerEnter={handleWarmup}
      onFocus={handleWarmup}
      onTouchStart={handleWarmup}
      aria-label={`${person.name} stories${person.isNew ? " (new)" : person.seen ? " (seen)" : ""}`}
      role="button"
      tabIndex={0}
    >
      <span
        className={`story-ring__gradient ${
          person.isNew
            ? "story-ring__gradient--new"
            : person.seen
              ? "story-ring__gradient--seen"
              : ""
        }`}
      >
        <span className="story-ring__inner">
          <img
            src={person.avatar}
            alt={person.name}
            className="story-ring__avatar"
            loading="lazy"
            onError={handleImageError}
          />
        </span>
      </span>
      <span className="story-ring__name">{person.name}</span>
    </button>
  );
});

StoryRing.propTypes = {
  person: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    avatar: PropTypes.string.isRequired,
    seen: PropTypes.bool,
    isNew: PropTypes.bool,
    stories: PropTypes.array.isRequired,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
};

StoryRing.displayName = "StoryRing";

const useVideoProgress = (videoRef, currentStoryId) => {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);
  const lastUpdateRef = useRef(0);

  useEffect(() => {
    setProgress(0);
    lastUpdateRef.current = Date.now();
    const video = videoRef.current;
    if (!video) return;

    const tick = () => {
      try {
        const now = Date.now();
        if (now - lastUpdateRef.current > PROGRESS_UPDATE_INTERVAL) {
          if (video.duration && !isNaN(video.duration)) {
            const newProgress = Math.min(
              1,
              Math.max(0, video.currentTime / video.duration),
            );
            setProgress(newProgress);
          }
          lastUpdateRef.current = now;
        }
        rafRef.current = requestAnimationFrame(tick);
      } catch (error) {
        console.warn("Error updating video progress:", error);
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [currentStoryId]);

  return progress;
};

const useVideoPrefetch = (src) => {
  useEffect(() => {
    prefetchVideo(src);
  }, [src]);
};

const StoryViewerModal = memo(
  ({ people, startPersonIndex, onClose, onViewRecipe }) => {
    if (
      !Array.isArray(people) ||
      people.length === 0 ||
      startPersonIndex < 0 ||
      startPersonIndex >= people.length
    ) {
      console.warn("Invalid props for StoryViewerModal");
      return null;
    }
    const [personIndex, setPersonIndex] = useState(startPersonIndex);
    const [storyIndex, setStoryIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const [muted, setMuted] = useState(false);
    const [videoLoading, setVideoLoading] = useState(true);
    const [videoError, setVideoError] = useState(false);
    const videoRef = useRef(null);
    const frameRef = useRef(null);
    const cardRef = useRef(null);
    const nextPreviewRef = useRef(null);
    const prevPreviewRef = useRef(null);

    const person = useMemo(() => people[personIndex], [people, personIndex]);
    const currentStory = useMemo(
      () => person?.stories?.[storyIndex],
      [person, storyIndex],
    );
    const progress = useVideoProgress(videoRef, currentStory?.id);

    const nextStorySrc = useMemo(() => {
      if (!person) return null;
      if (storyIndex + 1 < person.stories.length) {
        return person.stories[storyIndex + 1].video;
      }
      const nextPersonEntry = people[personIndex + 1];
      return nextPersonEntry?.stories?.[0]?.video || null;
    }, [person, people, personIndex, storyIndex]);

    useVideoPrefetch(nextStorySrc);

    useEffect(() => {
      setVideoLoading(true);
      setVideoError(false);
    }, [currentStory?.id]);

    const goNext = useCallback(() => {
      if (!person) return;
      if (storyIndex + 1 < person.stories.length) {
        setStoryIndex((s) => s + 1);
      } else if (personIndex + 1 < people.length) {
        setPersonIndex((p) => p + 1);
        setStoryIndex(0);
      } else {
        onClose?.();
      }
    }, [person, personIndex, people.length, storyIndex, onClose]);

    const goPrev = useCallback(() => {
      if (storyIndex > 0) {
        setStoryIndex((s) => s - 1);
        return;
      }
      if (personIndex > 0) {
        const prevPerson = people[personIndex - 1];
        setPersonIndex((p) => p - 1);
        setStoryIndex(prevPerson?.stories?.length - 1 || 0);
      }
    }, [people, personIndex, storyIndex]);

    const goNextPerson = useCallback(() => {
      if (personIndex + 1 < people.length) {
        setPersonIndex((p) => p + 1);
        setStoryIndex(0);
      } else {
        onClose?.();
      }
    }, [personIndex, people.length, onClose]);

    const goPrevPerson = useCallback(() => {
      if (personIndex > 0) {
        setPersonIndex((p) => p - 1);
        setStoryIndex(0);
      }
    }, [personIndex]);

    useEffect(() => {
      let lastKeyTime = 0;

      const handleKeyDown = (e) => {
        const now = Date.now();
        if (e.key === "Escape") {
          onClose?.();
          return;
        }

        if (now - lastKeyTime < KEYBOARD_THROTTLE_MS) {
          return;
        }

        switch (e.key) {
          case "ArrowRight":
            lastKeyTime = now;
            goNext();
            e.preventDefault();
            break;
          case "ArrowLeft":
            lastKeyTime = now;
            goPrev();
            e.preventDefault();
            break;
          default:
            break;
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [goNext, goPrev, onClose]);
    
    useEffect(() => {
      const frame = frameRef.current;
      if (!frame) return;

      const SWIPE_THRESHOLD = 1200; // minimum pixels to trigger swipe

      const handleTouchStart = (e) => {
        touchStartXRef.current = e.touches[0].clientX;
      };
      const handleTouchEnd = (e) => {
        if (touchStartXRef.current === null) return;

        const touchEndX = e.changedTouches[0].clientX;
        const swipeDistance = touchStartXRef.current - touchEndX;

        if (swipeDistance > SWIPE_THRESHOLD) {
          goNextPerson();
        }
        else if (swipeDistance < -SWIPE_THRESHOLD) {
          goPrevPerson();
        }

        touchStartXRef.current = null;
      };

      frame.addEventListener("touchstart", handleTouchStart);
      frame.addEventListener("touchend", handleTouchEnd);

      return () => {
        frame.removeEventListener("touchstart", handleTouchStart);
        frame.removeEventListener("touchend", handleTouchEnd);
      };
    }, [goNextPerson, goPrevPerson]);

    useEffect(() => {
      const frame = frameRef.current;
      const card = cardRef.current;
      if (!frame || !card) return;

      const SWIPE_THRESHOLD = 80;
      const W = () => frame.offsetWidth || 400;

      let startX = null;
      let dragging = false;

      const setLayers = (delta) => {
        const w = W();
        const clamped = Math.max(-w, Math.min(w, delta));
        const progress = Math.min(1, Math.abs(clamped) / w);

        card.style.transform = `translateX(${clamped}px) scale(${1 - progress * 0.05})`;
        card.style.opacity = String(1 - progress * 0.25);

        const next = nextPreviewRef.current;
        const prev = prevPreviewRef.current;
        if (next) next.style.transform = `translateX(${w + clamped}px)`;
        if (prev) prev.style.transform = `translateX(${-w + clamped}px)`;
      };

      const resetLayers = (animated) => {
        card.classList.toggle("story-modal__card--dragging", !animated);
        if (nextPreviewRef.current)
          nextPreviewRef.current.classList.toggle(
            "story-modal__preview-card--dragging",
            !animated,
          );
        if (prevPreviewRef.current)
          prevPreviewRef.current.classList.toggle(
            "story-modal__preview-card--dragging",
            !animated,
          );
        card.style.transform = "translateX(0px) scale(1)";
        card.style.opacity = "1";
        if (nextPreviewRef.current)
          nextPreviewRef.current.style.transform = `translateX(${W()}px)`;
        if (prevPreviewRef.current)
          prevPreviewRef.current.style.transform = `translateX(${-W()}px)`;
      };

      const handleStart = (clientX) => {
        startX = clientX;
        dragging = true;
        card.classList.add("story-modal__card--dragging");
        if (nextPreviewRef.current)
          nextPreviewRef.current.classList.add(
            "story-modal__preview-card--dragging",
          );
        if (prevPreviewRef.current)
          prevPreviewRef.current.classList.add(
            "story-modal__preview-card--dragging",
          );
      };

      const handleMove = (clientX) => {
        if (!dragging || startX === null) return;
        setLayers(clientX - startX);
      };

      const handleEnd = (clientX) => {
        if (!dragging || startX === null) {
          dragging = false;
          return;
        }
        const delta = clientX - startX;
        startX = null;
        dragging = false;

        if (Math.abs(delta) > SWIPE_THRESHOLD) {
          const w = W();
          const direction = delta < 0 ? -1 : 1;

          card.classList.remove("story-modal__card--dragging");
          if (nextPreviewRef.current)
            nextPreviewRef.current.classList.remove(
              "story-modal__preview-card--dragging",
            );
          if (prevPreviewRef.current)
            prevPreviewRef.current.classList.remove(
              "story-modal__preview-card--dragging",
            );

          card.style.transform = `translateX(${direction * w}px) scale(0.95)`;
          card.style.opacity = "0";
          const incoming =
            direction === -1 ? nextPreviewRef.current : prevPreviewRef.current;
          if (incoming) incoming.style.transform = "translateX(0px)";

          const onTransitionEnd = () => {
            card.removeEventListener("transitionend", onTransitionEnd);
            if (direction === -1) goNextPerson();
            else goPrevPerson();
            requestAnimationFrame(() => resetLayers(false));
          };
          card.addEventListener("transitionend", onTransitionEnd, {
            once: true,
          });
        } else {
          resetLayers(true);
        }
      };

      const handleTouchStart = (e) => handleStart(e.touches[0].clientX);
      const handleTouchMove = (e) => handleMove(e.touches[0].clientX);
      const handleTouchEnd = (e) => handleEnd(e.changedTouches[0].clientX);
      const handleMouseDown = (e) => handleStart(e.clientX);
      const handleMouseMove = (e) => handleMove(e.clientX);
      const handleMouseUp = (e) => handleEnd(e.clientX);
      resetLayers(false);
      frame.addEventListener("touchstart", handleTouchStart, { passive: true });
      frame.addEventListener("touchmove", handleTouchMove, { passive: true });
      frame.addEventListener("touchend", handleTouchEnd);
      frame.addEventListener("mousedown", handleMouseDown);
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        frame.removeEventListener("touchstart", handleTouchStart);
        frame.removeEventListener("touchmove", handleTouchMove);
        frame.removeEventListener("touchend", handleTouchEnd);
        frame.removeEventListener("mousedown", handleMouseDown);
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }, [goNextPerson, goPrevPerson, personIndex]);
    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;

      if (paused) {
        video.pause();
      } else {
        video.play().catch((error) => {
          console.warn("Failed to play video:", error);
        });
      }
    }, [paused]);

    const handleRecipeClick = useCallback(() => {
      const recipeKey = currentStory?.urlrecipe;
      if (!recipeKey) return;

      const recipe = findRecipeByKey(recipeKey);
      if (recipe && onViewRecipe) {
        onViewRecipe(recipe);
        return;
      }

      if (recipeKey) {
        window.open(recipeKey, "_blank");
      }
    }, [currentStory, onViewRecipe]);

    const handleMuteToggle = useCallback(() => {
      setMuted((m) => !m);
    }, []);

    const handlePauseToggle = useCallback(() => {
      setPaused((p) => !p);
    }, []);

    const handleVideoCanPlay = useCallback(() => {
      setVideoError(false);
    }, []);

    const handleVideoLoadedData = useCallback(() => {
      setVideoLoading(false);
      setVideoError(false);
    }, []);

    const handleVideoLoadStart = useCallback(() => {
      setVideoLoading(true);
    }, []);

    const handleVideoError = useCallback(
      (e) => {
        console.warn("Video playback error:", e);
        setVideoError(true);
        setVideoLoading(false);
        // setTimeout(goNext, 500);
      },
      [goNext],
    );

    if (!person || !currentStory) {
      return null;
    }

    const nextPerson = people[personIndex + 1] || null;
    const prevPerson = people[personIndex - 1] || null;

    return (
      <div className="story-modal">
        <div className="story-modal__frame" ref={frameRef}>
          {prevPerson && (
            <div
              className="story-modal__preview-card story-modal__preview-card--prev"
              ref={prevPreviewRef}
            >
              <img
                src={prevPerson.avatar}
                alt=""
                className="story-modal__preview-cover"
              />
              <div className="story-modal__preview-scrim" />
            </div>
          )}

          {nextPerson && (
            <div
              className="story-modal__preview-card story-modal__preview-card--next"
              ref={nextPreviewRef}
            >
              <img
                src={nextPerson.avatar}
                alt=""
                className="story-modal__preview-cover"
              />
              <div className="story-modal__preview-scrim" />
            </div>
          )}

          <div className="story-modal__card" ref={cardRef}>
            <div className="story-modal__progress">
              {person.stories.map((s, i) => (
                <div key={s.id} className="story-modal__progress-track">
                  <div
                    className="story-modal__progress-fill"
                    style={{
                      width:
                        i < storyIndex
                          ? "100%"
                          : i === storyIndex
                            ? `${progress * 100}%`
                            : "0%",
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="story-modal__header">
              <div className="story-modal__user">
                <img
                  src={person.avatar}
                  alt={person.name}
                  className="story-modal__avatar"
                  loading="lazy"
                />
                <span className="story-modal__name">{person.name}</span>
              </div>
              <div className="story-modal__controls">
                <button
                  onClick={handleMuteToggle}
                  className="story-modal__icon-btn"
                  aria-label={muted ? "Unmute" : "Mute"}
                >
                  {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
                <button
                  onClick={handlePauseToggle}
                  className="story-modal__icon-btn"
                  aria-label={paused ? "Play" : "Pause"}
                >
                  {paused ? <Play size={18} /> : <Pause size={18} />}
                </button>
                <button
                  onClick={onClose}
                  className="story-modal__icon-btn"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <video
              key={currentStory.id}
              ref={videoRef}
              src={currentStory.video}
              autoPlay
              muted={muted}
              playsInline
              preload={VIDEO_PRELOAD_STRATEGY}
              fetchpriority="high"
              onEnded={goNext}
              onError={handleVideoError}
              onCanPlay={handleVideoCanPlay}
              onLoadedData={handleVideoLoadedData}
              onLoadStart={handleVideoLoadStart}
              onLoadedMetadata={() => {
                if (!paused && videoRef.current) {
                  videoRef.current.play().catch((err) => {
                    console.warn("Autoplay failed:", err);
                  });
                }
              }}
              className="story-modal__video"
              style={{ display: videoLoading ? "none" : "block" }}
            />

            {videoLoading && (
              <div className="story-modal__preview">
                <div className="story-modal__loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading video...</p>
                </div>
              </div>
            )}

            {videoError && (
              <div className="story-modal__preview">
                <div className="story-modal__error-message">
                  <p>Unable to load video</p>
                </div>
              </div>
            )}

            {currentStory.urlrecipe && (
              <button
                type="button"
                onClick={handleRecipeClick}
                className="story-modal-recipe__button"
              >
                View the Recipe
              </button>
            )}
            <button
              onClick={goPrev}
              className="story-modal__tap-zone story-modal__tap-zone--left"
              aria-label="Previous story"
            />
            <button
              onClick={goNext}
              className="story-modal__tap-zone story-modal__tap-zone--right"
              aria-label="Next story"
            />
          </div>
        </div>
      </div>
    );
  },
);
function StoryTray({ onSelectRecipe = null }) {
  const [people, setPeople] = useState(() => loadPeopleState());
  const [openPersonIndex, setOpenPersonIndex] = useState(null);
  const [error, setError] = useState(null);

  const handleOpen = useCallback(
    (person) => {
      try {
        if (!person || typeof person.id === "undefined") {
          console.warn("Invalid person object");
          return;
        }
        const idx = people.findIndex((p) => p.id === person.id);
        if (idx >= 0) {
          setPeople((prev) => {
            const updated = prev.map((p) =>
              p.id === person.id ? { ...p, seen: true, isNew: false } : p,
            );
            const seenIds = updated.filter((p) => p.seen).map((p) => p.id);
            setPersistedSeenIds(seenIds);
            const knownIds = new Set(getPersistedKnownStoryIds() || []);
            person.stories.forEach((s) => knownIds.add(s.id));
            setPersistedKnownStoryIds(Array.from(knownIds));

            return updated;
          });
          setOpenPersonIndex(idx);
        }
      } catch (err) {
        console.error("Error opening story:", err);
        setError("Failed to open story");
      }
    },
    [people],
  );

  useEffect(() => {
    people.slice(0, 2).forEach((p) => {
      const firstVideo = p?.stories?.[0]?.video;
      if (firstVideo) prefetchVideo(firstVideo);
    });
  }, [people]);

  const handleClose = useCallback(() => {
    setOpenPersonIndex(null);
  }, []);

  const handleViewRecipe = useCallback(
    (recipe) => {
      try {
        if (onSelectRecipe && recipe) {
          onSelectRecipe(recipe);
        }
        setOpenPersonIndex(null);
      } catch (err) {
        console.error("Error viewing recipe:", err);
        setError("Failed to open recipe");
      }
    },
    [onSelectRecipe],
  );

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (!Array.isArray(people)) {
    return <div className="story-tray">No stories available</div>;
  }

  return (
    <div className="story-tray">
      {error && (
        <div className="story-tray__error" role="alert">
          {error}
        </div>
      )}

      <div className="story-tray__row" role="list">
        {people.length > 0 ? (
          people.map((person) => (
            <StoryRing key={person.id} person={person} onOpen={handleOpen} />
          ))
        ) : (
          <p className="story-tray__empty">No stories available</p>
        )}
      </div>

      {openPersonIndex !== null && (
        <StoryViewerModal
          people={people}
          startPersonIndex={openPersonIndex}
          onClose={handleClose}
          onViewRecipe={handleViewRecipe}
        />
      )}
    </div>
  );
}

StoryTray.propTypes = {
  onSelectRecipe: PropTypes.func,
};
StoryTray.displayName = "StoryTray";

export default memo(StoryTray);
